import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUpdatePageTitle } from "@cynaps/core";
import { Space } from "../../../components/Space/Space";
import { cn } from "../../../utils/bem";
import "./PeopleInvitation.scss";
import { PeopleList } from "./PeopleList";
import "./PeoplePage.scss";
import { OrganizationApiKeyModal } from "./OrganizationApiKeyModal";
import { IconPlus, IconInfoOutline } from "@cynaps/icons";
import { InviteMembersDialog } from "../../../components/InviteMembersDialog/InviteMembersDialog";
import { useAuth } from "@cynaps/core/providers/AuthProvider";
import { useToast } from "@cynaps/ui";
import { SelectedUser } from "./SelectedUser";
import { useAPI } from "../../../providers/ApiProvider";

export const PeoplePage = () => {
  const api = useAPI();
  const peopleListRef = useRef();
  const toast = useToast();
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserRole, setSelectedUserRole] = useState(null);
  const [invitationOpen, setInvitationOpen] = useState(false);
  const [apiKeyModalOpen, setApiKeyModalOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const { user } = useAuth();
  // Ensure we get the ID correctly, handling potential object structure variations
  const organizationId = user?.active_organization?.id || user?.active_organization;

  // Check if current user is admin or owner
  const canManageApiKey = currentUserRole === "owner" || currentUserRole === "admin";

  useUpdatePageTitle("People");

  // Fetch current user's role in the organization
  useEffect(() => {
    const fetchUserRole = async () => {
      if (!organizationId || !user?.id) return;
      try {
        const response = await api.callApi("memberships", {
          params: { pk: organizationId },
        });
        const membership = response.results?.find((m) => m.user.id === user.id);
        if (membership) {
          setCurrentUserRole(membership.role);
        }
      } catch (err) {
        console.error("Failed to fetch user role:", err);
      }
    };
    fetchUserRole();
  }, [api, organizationId, user?.id]);

  const selectUser = useCallback(
    (user, role) => {
      setSelectedUser(user);
      setSelectedUserRole(role);

      localStorage.setItem("selectedUser", user?.id);
    },
    [setSelectedUser, setSelectedUserRole],
  );

  const handleMemberRemoved = useCallback(() => {
    // Trigger refresh of the people list
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  const showApiKeyModal = useCallback(() => {
    setApiKeyModalOpen(true);
    if (window.__ca) {
      window.__ca("organization.api_key");
    }
  }, []);

  const defaultSelected = useMemo(() => {
    return localStorage.getItem("selectedUser");
  }, []);

  // Premium button style matching design system
  const buttonStyle = {
    background: "rgba(139, 92, 246, 0.15)",
    border: "1px solid rgba(139, 92, 246, 0.3)",
    borderRadius: "10px",
    color: "#c4b5fd",
    fontWeight: 600,
    fontSize: "13px",
    fontFamily: "'Space Grotesk', system-ui, sans-serif",
    height: "34px",
    padding: "0 16px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  };

  return (
    <div className={cn("people").toClassName()}>
      <div className={cn("people").elem("header-actions").toClassName()}>
        {/* API Key button - visible to all members */}
        <button
          type="button"
          onClick={showApiKeyModal}
          aria-label="Organization API Key"
          className={cn("people").elem("action-btn").toClassName()}
        >
          <IconInfoOutline style={{ width: "15px", height: "15px", flexShrink: 0 }} />
          <span>API Key</span>
        </button>
        <button
          type="button"
          onClick={() => setInvitationOpen(true)}
          aria-label="Invite new member"
          className={cn("people").elem("action-btn").mod({ primary: true }).toClassName()}
        >
          <IconPlus style={{ width: "15px", height: "15px", flexShrink: 0 }} />
          <span>Add Members</span>
        </button>
      </div>
      <div className={cn("people").elem("content").toClassName()}>
        <PeopleList
          ref={peopleListRef}
          selectedUser={selectedUser}
          defaultSelected={defaultSelected}
          onSelect={(user, role) => selectUser(user, role)}
          refreshTrigger={refreshTrigger}
        />

        <SelectedUser
          user={selectedUser}
          memberRole={selectedUserRole}
          onClose={() => selectUser(null, null)}
          onMemberRemoved={handleMemberRemoved}
        />
      </div>
      <InviteMembersDialog
        isOpen={invitationOpen}
        onClose={() => setInvitationOpen(false)}
        organizationId={organizationId}
      />

      {/* API Key Modal */}
      {apiKeyModalOpen && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setApiKeyModalOpen(false);
          }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.7)",
            backdropFilter: "blur(8px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "linear-gradient(135deg, #0a0a0a 0%, #111111 100%)",
              border: "1px solid rgba(139, 92, 246, 0.3)",
              borderRadius: "16px",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 40px rgba(139, 92, 246, 0.15)",
            }}
          >
            {(() => {
              if (window.__ca) {
                window.__ca("organization.people.view", {
                  organization: organizationId,
                });
              }
              return null;
            })()}
            <OrganizationApiKeyModal
              organizationId={organizationId}
              canManage={canManageApiKey}
              onClose={() => setApiKeyModalOpen(false)}
              onSaved={() => {
                toast.show({ message: "API Key updated successfully" });
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

PeoplePage.title = "People";
PeoplePage.path = "/";
